/**
 * Port of MPA autofill hardening: browsers restore saved logins/passwords into
 * filters and confirmation fields. Harden attributes + clear untouched filters.
 *
 * Important: mark fields as user-edited only on trusted typing — never on
 * focusin alone. Autofill often focuses a filter, fills a username/email, and
 * would otherwise permanently stick (SPA pages mount after boot timeouts).
 */

const SKIP_TYPES = new Set([
  "hidden",
  "checkbox",
  "radio",
  "file",
  "button",
  "submit",
  "reset",
  "image",
  "range",
  "color",
]);

function fieldType(el: HTMLElement): string {
  if (el instanceof HTMLInputElement) {
    return String(el.getAttribute("type") || el.type || "text").toLowerCase();
  }
  return "text";
}

function isProtectedField(
  el: HTMLInputElement | HTMLTextAreaElement,
): boolean {
  return (
    el.getAttribute("data-hm-filter") != null ||
    el.getAttribute("data-hm-nofill") != null
  );
}

function hardenField(el: Element): void {
  if (!(el instanceof HTMLElement)) {
    return;
  }
  const tag = el.tagName;
  if (tag === "FORM") {
    el.setAttribute("autocomplete", "off");
    return;
  }
  if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") {
    return;
  }

  if (el.getAttribute("data-hm-allow-autofill") === "1") {
    return;
  }

  const type = fieldType(el);
  if (SKIP_TYPES.has(type)) {
    return;
  }

  const existing = String(el.getAttribute("autocomplete") || "").toLowerCase();
  if (existing === "username" || existing === "email" || existing === "current-password") {
    return;
  }

  const isPassword =
    type === "password" || el.classList.contains("input--secret");
  el.setAttribute("autocomplete", isPassword ? "new-password" : "off");
  el.setAttribute("autocorrect", "off");
  el.setAttribute("autocapitalize", "off");
  el.setAttribute("spellcheck", "false");
  el.setAttribute("data-lpignore", "true");
  el.setAttribute("data-1p-ignore", "true");
  el.setAttribute("data-bwignore", "true");
  el.setAttribute("data-form-type", "other");

  if ((tag === "INPUT" || tag === "TEXTAREA") && !el.dataset.autofillGuard) {
    el.dataset.autofillGuard = "1";
  }
}

function hardenTree(root: ParentNode | Element): void {
  if (root instanceof Element) {
    hardenField(root);
  }
  root.querySelectorAll?.("form, input, textarea, select").forEach(hardenField);
}

function clearFieldValue(input: HTMLInputElement | HTMLTextAreaElement): void {
  if (!input.value) {
    return;
  }
  input.value = "";
  // Sync React controlled inputs that listen to native `input`.
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

function clearUnwantedAutofill(): void {
  document
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      "input[data-hm-filter], textarea[data-hm-filter], input[data-hm-nofill]",
    )
    .forEach((input) => {
      if (input.dataset.hmUserEdited === "1") {
        return;
      }
      // Clear even when focused: autofill often focuses the field first.
      clearFieldValue(input);
    });
}

function isIntentionalEditEvent(event: Event): boolean {
  if (event instanceof KeyboardEvent) {
    const key = event.key;
    // Autofill does not synthesize printable keydowns; ignore modifiers/navigation.
    return key.length === 1 || key === "Backspace" || key === "Delete";
  }
  if (event.type === "paste") {
    return true;
  }
  // Playwright fill()/type() and real typing use insertText. Browser autofill
  // typically uses insertReplacementText or a bare input with empty inputType —
  // those must keep being cleared.
  if (event instanceof InputEvent) {
    const inputType = event.inputType || "";
    return (
      inputType === "insertText" ||
      inputType === "insertFromPaste" ||
      inputType === "insertFromDrop" ||
      inputType.startsWith("delete")
    );
  }
  return false;
}

function markTrustedUserEdit(event: Event): void {
  if (!event.isTrusted) {
    return;
  }
  const target = event.target;
  if (
    !(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)
  ) {
    return;
  }
  if (!isProtectedField(target)) {
    return;
  }
  if (!isIntentionalEditEvent(event)) {
    return;
  }

  target.dataset.hmUserEdited = "1";
}

let installed = false;
const pendingTimers: number[] = [];
let mutationObserver: MutationObserver | null = null;

function schedule(ms: number, fn: () => void): void {
  const id = window.setTimeout(() => {
    const idx = pendingTimers.indexOf(id);
    if (idx >= 0) {
      pendingTimers.splice(idx, 1);
    }
    if (typeof document === "undefined") {
      return;
    }
    fn();
  }, ms);
  pendingTimers.push(id);
}

/** Test helper: cancel deferred work so Vitest teardown stays clean. */
export function resetBrowserAutofillGuardForTests(): void {
  for (const id of pendingTimers.splice(0)) {
    window.clearTimeout(id);
  }
  mutationObserver?.disconnect();
  mutationObserver = null;
  installed = false;
}

/** Install once at app boot. Safe to call repeatedly. */
export function installBrowserAutofillGuard(): void {
  if (installed || typeof document === "undefined") {
    return;
  }
  installed = true;

  hardenTree(document);
  clearUnwantedAutofill();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      hardenTree(document);
      clearUnwantedAutofill();
    });
  }

  window.addEventListener("load", clearUnwantedAutofill);
  window.addEventListener("pageshow", clearUnwantedAutofill);
  [0, 50, 150, 400, 1000, 2000, 3500].forEach((ms) => {
    schedule(ms, () => {
      hardenTree(document);
      clearUnwantedAutofill();
    });
  });

  if (typeof MutationObserver !== "undefined") {
    let clearScheduled = false;
    mutationObserver = new MutationObserver((mutations) => {
      let addedProtected = false;
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) {
            return;
          }
          const el = node as Element;
          hardenTree(el);
          if (
            (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) &&
            isProtectedField(el)
          ) {
            addedProtected = true;
          } else if (
            el.querySelector?.(
              "input[data-hm-filter], textarea[data-hm-filter], input[data-hm-nofill]",
            )
          ) {
            addedProtected = true;
          }
        });
      }
      if (addedProtected && !clearScheduled) {
        clearScheduled = true;
        // Autofill often lands just after the node appears.
        schedule(0, () => {
          clearScheduled = false;
          clearUnwantedAutofill();
        });
        schedule(100, clearUnwantedAutofill);
        schedule(400, clearUnwantedAutofill);
      }
    });
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Lock after real typing/paste/insertText — never on focus or autofill alone.
  document.addEventListener("keydown", markTrustedUserEdit, true);
  document.addEventListener("paste", markTrustedUserEdit, true);
  document.addEventListener("beforeinput", markTrustedUserEdit, true);
  document.addEventListener("input", markTrustedUserEdit, true);
}
